import { useState } from 'react'
import { plansService } from '../services/plansService'
import { useDataFetch, useMutation } from '../hooks/useDataFetch'

const INTERVALS = ['month', 'year']

function formatAmount(amountMinor, currency) {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: String(currency || 'gbp').toUpperCase(),
    }).format(Number(amountMinor || 0) / 100)
}

/**
 * Plans Page — manages the live Stripe price(s) behind the TenderCentriX Discovery product.
 * Saving creates a new Price and archives the previous active one for that
 * interval (Stripe prices are immutable on amount/currency/interval).
 */
export default function Plans() {
    const { data: plans, loading, error, refetch } = useDataFetch(plansService.getAll)
    const { mutate: save, loading: saving } = useMutation(plansService.save)
    const { mutate: setActive, loading: toggling } = useMutation(plansService.setActive)

    const [form, setForm] = useState({ interval: 'month', amount: '', currency: 'gbp', nickname: '' })
    const [formError, setFormError] = useState(null)

    const activeByInterval = Object.fromEntries(
        INTERVALS.map((iv) => [iv, plans?.find((p) => p.interval === iv && p.active) || null])
    )

    const handleSave = async (e) => {
        e.preventDefault()
        setFormError(null)
        const amount = Number(form.amount)
        if (!Number.isFinite(amount) || amount <= 0) {
            setFormError('Enter a valid amount greater than 0')
            return
        }
        try {
            await save({
                interval: form.interval,
                amountMinor: Math.round(amount * 100),
                currency: form.currency,
                nickname: form.nickname || undefined,
            })
            setForm({ interval: form.interval, amount: '', currency: 'gbp', nickname: '' })
            refetch()
        } catch (err) {
            setFormError(err.message || 'Failed to save plan')
        }
    }

    const handleToggle = async (priceId, nextActive) => {
        await setActive(priceId, nextActive)
        refetch()
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Plans</h1>
                <button onClick={refetch} className="btn-secondary text-xs">Refresh</button>
            </div>

            {/* Currently live prices */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {INTERVALS.map((iv) => {
                    const plan = activeByInterval[iv]
                    return (
                        <div key={iv} className="card-admin p-5">
                            <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">Live {iv}ly Price</p>
                            {plan ? (
                                <>
                                    <p className="text-2xl font-bold mt-2 tracking-tight text-brand-navy">
                                        {formatAmount(plan.amountMinor, plan.currency)}
                                        <span className="text-sm font-medium text-admin-muted"> / {iv}</span>
                                    </p>
                                    <p className="text-[10px] text-admin-muted font-mono mt-1">{plan.id}</p>
                                </>
                            ) : (
                                <p className="text-sm text-admin-muted mt-2">No active price configured — checkout will fail for this interval.</p>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Create/replace a price */}
            <div className="card-admin p-5">
                <p className="text-sm font-bold text-admin-heading mb-3">Set New Price</p>
                <form onSubmit={handleSave} className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-[10px] font-bold text-admin-muted uppercase tracking-wide mb-1">Interval</label>
                        <select
                            value={form.interval}
                            onChange={(e) => setForm((f) => ({ ...f, interval: e.target.value }))}
                            className="px-3 py-1.5 text-sm bg-white border border-admin-border rounded focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                        >
                            {INTERVALS.map((iv) => <option key={iv} value={iv}>{iv === 'month' ? 'Monthly' : 'Yearly'}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-admin-muted uppercase tracking-wide mb-1">Amount (£)</label>
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                            placeholder="95.00"
                            className="w-32 px-3 py-1.5 text-sm bg-white border border-admin-border rounded focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-admin-muted uppercase tracking-wide mb-1">Nickname (optional)</label>
                        <input
                            type="text"
                            value={form.nickname}
                            onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
                            placeholder="e.g. 2026 price rise"
                            className="w-56 px-3 py-1.5 text-sm bg-white border border-admin-border rounded focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                        />
                    </div>
                    <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save Price'}
                    </button>
                </form>
                {formError && <p className="text-xs text-red-600 font-medium mt-2">{formError}</p>}
                <p className="text-[10px] text-admin-muted mt-2">
                    Saving creates a new Stripe price and archives the current active one for this interval — existing subscribers are unaffected until they renew or change plan.
                </p>
            </div>

            {/* History */}
            <div className="card-admin">
                <div className="px-5 py-4 border-b border-admin-border bg-gray-50">
                    <span className="text-sm font-bold text-admin-heading">Price History</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="table-admin">
                        <thead>
                            <tr>
                                <th className="px-6 py-3">Price ID</th>
                                <th className="px-6 py-3">Interval</th>
                                <th className="px-6 py-3 text-right">Amount</th>
                                <th className="px-6 py-3">Nickname</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan={6} className="p-6 text-center text-admin-muted font-bold">Loading plans...</td></tr>
                            ) : error ? (
                                <tr><td colSpan={6} className="p-6 text-center text-red-600 font-bold">{error}</td></tr>
                            ) : !plans?.length ? (
                                <tr><td colSpan={6} className="p-6 text-center text-admin-muted font-bold">No Stripe prices found.</td></tr>
                            ) : (
                                plans.map((p) => (
                                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-3 font-mono text-xs text-admin-text">{p.id}</td>
                                        <td className="px-6 py-3 text-admin-text font-medium capitalize">{p.interval || '—'}</td>
                                        <td className="px-6 py-3 text-right font-bold text-admin-text">{formatAmount(p.amountMinor, p.currency)}</td>
                                        <td className="px-6 py-3 text-admin-muted">{p.nickname || '—'}</td>
                                        <td className="px-6 py-3">
                                            <span className={`badge ${p.active ? 'badge-success' : 'badge-neutral'}`}>
                                                {p.active ? 'Active' : 'Archived'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                            <button
                                                onClick={() => handleToggle(p.id, !p.active)}
                                                disabled={toggling}
                                                className="text-xs font-bold text-brand-navy hover:underline disabled:opacity-50"
                                            >
                                                {p.active ? 'Archive' : 'Reactivate'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
